import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe2, CheckCircle, Clock, Loader2, Search, Filter, RefreshCw,
  ExternalLink, Save, Lock, X, AlertTriangle, User, ChevronDown, ChevronUp,
  Timer, TrendingUp, ShieldAlert, CalendarClock, BarChart3, ClipboardList, AlertOctagon
} from 'lucide-react';
import { cxDashboardAPI, settingsAPI, otaAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { saveFilters, loadFilters, clearPageFilters } from '../utils/filterPersistence';
import { OTAChecklistBadge } from '../components/OTAChecklistSLATracker';

const STATUS_STYLES = {
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  assigned: 'bg-blue-100 text-blue-700 border-blue-200',
  pending_assignment: 'bg-red-100 text-red-700 border-red-200',
  not_started: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_LABELS = {
  completed: 'Completed', in_progress: 'In Progress', assigned: 'Assigned',
  pending_assignment: 'Needs Assignment', not_started: 'Not Started',
};

const SLA_BADGE = {
  on_time: { label: 'On Time', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  late: { label: 'Late', cls: 'bg-red-100 text-red-700 border-red-200' },
  overdue: { label: 'Overdue', cls: 'bg-red-100 text-red-700 border-red-200' },
  on_track: { label: 'On Track', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
};

const OTA_PLATFORMS = ['Airbnb', 'Agoda', 'Expedia', 'MMT', 'Goibibo', 'Hotels_Com', 'Booking_Com', 'Trip_com', 'Priceline', 'Tripadvisor', 'EasemyTrip', 'Cleartrip'];

const TIME_PRESETS = [
  { label: 'All Time', key: 'all' },
  { label: 'Last 7 days', key: '7d' },
  { label: 'This Month', key: 'this_month' },
  { label: 'Custom', key: 'custom' },
];

function getDateRange(key) {
  const now = new Date();
  const fmt = d => d.toISOString().split('T')[0];
  if (key === '7d') { const f = new Date(now); f.setDate(now.getDate() - 7); return { date_from: fmt(f), date_to: fmt(now) }; }
  if (key === 'this_month') { return { date_from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), date_to: fmt(now) }; }
  return {};
}

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function fmtDateShort(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch { return iso; }
}

const SlaBadge = ({ status }) => {
  const info = SLA_BADGE[status];
  if (!info) return null;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${info.cls}`} data-testid={`sla-badge-${status}`}>{info.label}</span>;
};

// --- User Performance Widget ---
const UserPerformanceWidget = ({ data }) => {
  const [sortBy, setSortBy] = useState('completed');
  if (!data || data.length === 0) return null;

  const sorted = [...data].sort((a, b) => {
    if (sortBy === 'completed') return (b.completed || 0) - (a.completed || 0);
    if (sortBy === 'late') return (b.late || 0) - (a.late || 0);
    if (sortBy === 'avg_hours') return (b.avg_hours || 0) - (a.avg_hours || 0);
    return (b.assigned || 0) - (a.assigned || 0);
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="user-performance-widget">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-teal-500" />
          <h3 className="text-sm font-semibold text-gray-800">User Performance Comparison</h3>
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white" data-testid="user-perf-sort">
          <option value="completed">Sort by Completed</option>
          <option value="assigned">Sort by Assigned</option>
          <option value="late">Sort by Late</option>
          <option value="avg_hours">Sort by Avg Time</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">User</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Assigned</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Completed</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Late</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500">Avg Time</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500">Completion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map(u => {
              const pct = u.assigned > 0 ? Math.round((u.completed / u.assigned) * 100) : 0;
              return (
                <tr key={u.user} className="hover:bg-gray-50" data-testid={`user-perf-row-${u.user}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-700">
                        {(u.user || '?')[0].toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-800 text-xs">{u.user}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-center font-medium text-gray-700">{u.assigned}</td>
                  <td className="px-4 py-2.5 text-center font-medium text-emerald-600">{u.completed}</td>
                  <td className="px-4 py-2.5 text-center">
                    {u.late > 0 ? <span className="font-medium text-red-600">{u.late}</span> : <span className="text-gray-400">0</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center text-gray-700">{u.avg_hours != null ? `${u.avg_hours}h` : '-'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Inline edit row with SLA columns
const EditableOTARow = ({ item, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isLocked = item.assignment_status === 'completed' || item.locked;

  const startEdit = () => {
    setEditing(true);
    setForm({ hotel_id: item.hotel_id, listing_url: item.listing_url, property_name_on_ota: item.property_name_on_ota, access_to_hotel: item.access_to_hotel });
  };

  const handleSave = async (markComplete = false) => {
    setSaving(true);
    try {
      await onSave(item.lead_id, item.ota_index, { ...form, mark_completed: markComplete });
      setEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <tr className="hover:bg-gray-50 transition-colors" data-testid={`ota-assign-row-${item.lead_id}-${item.ota_index}`}>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Globe2 size={14} className="text-teal-500 shrink-0" />
          <span className="font-medium text-gray-800 text-xs">{item.ota_name}</span>
        </div>
      </td>
      <td className="px-3 py-2.5 text-gray-700 text-xs">
        <span className="font-medium">{item.hotel_name}</span>
        <span className="text-gray-400 ml-1 text-[10px]">{item.lead_id.slice(0, 8)}</span>
      </td>
      <td className="px-3 py-2.5 text-gray-600 text-xs">{item.assigned_to || <span className="text-gray-400 italic">None</span>}</td>
      <td className="px-3 py-2.5">
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUS_STYLES[item.assignment_status]}`}>
          {STATUS_LABELS[item.assignment_status]}
        </span>
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-500">{fmtDateShort(item.assigned_at)}</td>
      <td className="px-3 py-2.5 text-xs">
        <span className={item.sla_status === 'overdue' ? 'text-red-600 font-medium' : 'text-gray-500'}>{fmtDateShort(item.deadline_at)}</span>
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-500">{fmtDateShort(item.completed_at)}</td>
      <td className="px-3 py-2.5"><SlaBadge status={item.sla_status} /></td>
      <td className="px-3 py-2.5 text-xs text-gray-600">{item.time_taken_hours != null ? `${item.time_taken_hours}h` : '-'}</td>
      <td className="px-3 py-2.5">
        {editing ? (
          <input type="text" value={form.hotel_id || ''} onChange={e => setForm(f => ({ ...f, hotel_id: e.target.value }))}
            className="w-full px-2 py-1 border border-gray-200 rounded text-xs" placeholder="Hotel ID" data-testid={`ota-edit-hid-${item.ota_index}`} />
        ) : (
          <span className={`text-xs ${item.hotel_id ? 'text-gray-700' : 'text-red-400 italic'}`}>{item.hotel_id || 'Missing'}</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {editing ? (
          <input type="text" value={form.listing_url || ''} onChange={e => setForm(f => ({ ...f, listing_url: e.target.value }))}
            className="w-full px-2 py-1 border border-gray-200 rounded text-xs" placeholder="URL" data-testid={`ota-edit-url-${item.ota_index}`} />
        ) : (
          item.listing_url ? (
            <a href={item.listing_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline text-xs flex items-center gap-1 max-w-[120px] truncate">
              <ExternalLink size={10} className="shrink-0" />{item.listing_url}
            </a>
          ) : <span className="text-red-400 italic text-xs">Missing</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {isLocked ? (
          <Lock size={14} className="text-emerald-500" />
        ) : editing ? (
          <div className="flex gap-1">
            <button onClick={() => handleSave(false)} disabled={saving} className="p-1 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50" data-testid={`ota-save-${item.ota_index}`}>
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            </button>
            <button onClick={() => handleSave(true)} disabled={saving || !form.hotel_id || !form.listing_url}
              className="p-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50" title="Send to QC" data-testid={`ota-complete-${item.ota_index}`}>
              <CheckCircle size={12} />
            </button>
            <button onClick={() => setEditing(false)} className="p-1 border border-gray-200 rounded text-gray-500 hover:bg-gray-50"><X size={12} /></button>
          </div>
        ) : (
          <button onClick={startEdit} className="text-xs text-teal-600 hover:text-teal-800 font-medium" data-testid={`ota-edit-${item.ota_index}`}>Edit</button>
        )}
      </td>
    </tr>
  );
};

const TABLE_HEADERS = [
  { label: 'Platform', key: 'platform' },
  { label: 'Lead', key: 'lead' },
  { label: 'Assigned To', key: 'assigned_to' },
  { label: 'Status', key: 'status' },
  { label: 'Assigned', key: 'assigned_at' },
  { label: 'Deadline', key: 'deadline' },
  { label: 'Completed', key: 'completed_at' },
  { label: 'SLA', key: 'sla' },
  { label: 'Time', key: 'time' },
  { label: 'Hotel ID', key: 'hotel_id' },
  { label: 'URL', key: 'url' },
  { label: 'Actions', key: 'actions' },
];

// --- Main OTA Dashboard ---
const OTADashboard = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [groupBy, setGroupBy] = useState('none');
  const [checklistAnalytics, setChecklistAnalytics] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Filters - restore from localStorage
  const _sf = loadFilters(user?.id, 'ota_dashboard');
  const [timePreset, setTimePreset] = useState(_sf?.timePreset || 'all');
  const [customFrom, setCustomFrom] = useState(_sf?.customFrom || '');
  const [customTo, setCustomTo] = useState(_sf?.customTo || '');
  const [userFilter, setUserFilter] = useState(_sf?.userFilter || '');
  const [otaStatusFilter, setOtaStatusFilter] = useState(_sf?.otaStatusFilter || '');
  const [otaPlatformFilter, setOtaPlatformFilter] = useState(_sf?.otaPlatformFilter || '');
  const [completenessFilter, setCompletenessFilter] = useState(_sf?.completenessFilter || '');
  // SLA Filters
  const [deadlineStatusFilter, setDeadlineStatusFilter] = useState(_sf?.deadlineStatusFilter || '');
  const [assignedFrom, setAssignedFrom] = useState(_sf?.assignedFrom || '');
  const [assignedToDate, setAssignedToDate] = useState(_sf?.assignedToDate || '');
  const [completedFrom, setCompletedFrom] = useState(_sf?.completedFrom || '');
  const [completedToDate, setCompletedToDate] = useState(_sf?.completedToDate || '');

  // Persist filters on change
  useEffect(() => {
    saveFilters(user?.id, 'ota_dashboard', { timePreset, customFrom, customTo, userFilter, otaStatusFilter, otaPlatformFilter, completenessFilter, deadlineStatusFilter, assignedFrom, assignedToDate, completedFrom, completedToDate });
  }, [timePreset, customFrom, customTo, userFilter, otaStatusFilter, otaPlatformFilter, completenessFilter, deadlineStatusFilter, assignedFrom, assignedToDate, completedFrom, completedToDate, user?.id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getUsers();
        setUsers(Array.isArray(res.data) ? res.data : res.data?.users || []);
      } catch {}
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize, search, group_by: groupBy };
      if (userFilter) params.user_filter = userFilter;
      if (otaStatusFilter) params.ota_status = otaStatusFilter;
      if (otaPlatformFilter) params.ota_platform = otaPlatformFilter;
      if (completenessFilter) params.completeness = completenessFilter;
      if (deadlineStatusFilter) params.deadline_status = deadlineStatusFilter;
      if (assignedFrom) params.assigned_from = assignedFrom;
      if (assignedToDate) params.assigned_to_date = assignedToDate;
      if (completedFrom) params.completed_from = completedFrom;
      if (completedToDate) params.completed_to_date = completedToDate;
      if (timePreset === 'custom') {
        if (customFrom) params.date_from = customFrom;
        if (customTo) params.date_to = customTo;
      } else if (timePreset !== 'all') {
        const range = getDateRange(timePreset);
        if (range.date_from) params.date_from = range.date_from;
        if (range.date_to) params.date_to = range.date_to;
      }
      const res = await cxDashboardAPI.getOTAAssignments(params);
      setAssignments(res.data.assignments || []);
      setTotalPages(res.data.total_pages || 1);
      setAnalytics(res.data.analytics || {});
    } catch (err) { console.error('OTA Dashboard error:', err); }
    finally { setLoading(false); }
  }, [page, search, userFilter, otaStatusFilter, otaPlatformFilter, completenessFilter, timePreset, customFrom, customTo, groupBy, deadlineStatusFilter, assignedFrom, assignedToDate, completedFrom, completedToDate, pageSize]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load checklist analytics
  useEffect(() => {
    (async () => {
      try {
        const res = await otaAPI.getChecklistAnalytics();
        setChecklistAnalytics(res.data);
      } catch {}
    })();
  }, [loading]); // Refresh when main data reloads

  const handleSave = async (leadId, otaIdx, data) => {
    await cxDashboardAPI.completeOTA(leadId, otaIdx, data);
    loadData();
  };

  const a = analytics;
  const hasActiveFilters = userFilter || otaStatusFilter || otaPlatformFilter || completenessFilter || timePreset !== 'all' || deadlineStatusFilter || assignedFrom || assignedToDate || completedFrom || completedToDate;

  const clearAllFilters = () => {
    setUserFilter(''); setOtaStatusFilter(''); setOtaPlatformFilter('');
    setCompletenessFilter(''); setTimePreset('all'); setDeadlineStatusFilter('');
    setAssignedFrom(''); setAssignedToDate(''); setCompletedFrom(''); setCompletedToDate('');
    setPage(1);
    clearPageFilters(user?.id, 'ota_dashboard');
  };

  // Group data
  const grouped = groupBy !== 'none' ? assignments.reduce((acc, item) => {
    const key = groupBy === 'user' ? (item.assigned_to || 'Unassigned') : (item.hotel_name || 'Unknown');
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {}) : null;

  const toggleGroup = (key) => setExpandedGroups(p => ({ ...p, [key]: !p[key] }));

  const tableHead = (
    <thead className="bg-gray-50 border-b border-gray-200">
      <tr>
        {TABLE_HEADERS.map(h => (
          <th key={h.key} className="px-3 py-2 text-left text-xs font-semibold text-gray-500">{h.label}</th>
        ))}
      </tr>
    </thead>
  );

  return (
    <div className="space-y-5" data-testid="ota-dashboard">
      {/* Global Filter Bar - TOP */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {TIME_PRESETS.map(p => (
                <button key={p.key} onClick={() => { setTimePreset(p.key); setPage(1); }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${timePreset === p.key ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid={`ota-time-${p.key}`}>{p.label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs bg-white" data-testid="ota-group-by">
              <option value="none">No Grouping</option>
              <option value="user">Group by User</option>
              <option value="lead">Group by Lead</option>
            </select>
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${showFilters || hasActiveFilters ? 'bg-teal-50 border-teal-200 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid="ota-dash-toggle-filters">
              <Filter size={14} /> Filters {hasActiveFilters && <span className="w-2 h-2 bg-teal-500 rounded-full" />}
            </button>
            <button onClick={loadData} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="ota-dash-refresh">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {timePreset === 'custom' && (
          <div className="px-5 pb-3 flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Lead Date:</span>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="ota-custom-from" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="ota-custom-to" />
          </div>
        )}
        {showFilters && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 space-y-3" data-testid="ota-dash-filter-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assigned User</label>
                <select value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="ota-dash-user-filter">
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.email} value={u.name || u.email}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">OTA Status</label>
                <select value={otaStatusFilter} onChange={e => { setOtaStatusFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="ota-dash-status-filter">
                  <option value="">All Statuses</option>
                  <option value="pending_assignment">Needs Assignment</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">OTA Platform</label>
                <select value={otaPlatformFilter} onChange={e => { setOtaPlatformFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="ota-dash-platform-filter">
                  <option value="">All Platforms</option>
                  {OTA_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Completeness</label>
                <select value={completenessFilter} onChange={e => { setCompletenessFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="ota-dash-completeness-filter">
                  <option value="">All</option>
                  <option value="incomplete">Incomplete</option>
                  <option value="complete">Complete</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">SLA Status</label>
                <select value={deadlineStatusFilter} onChange={e => { setDeadlineStatusFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="ota-dash-deadline-status-filter">
                  <option value="">All</option>
                  <option value="on_time">On Time</option>
                  <option value="late">Late</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assigned From</label>
                <input type="date" value={assignedFrom} onChange={e => { setAssignedFrom(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="ota-dash-assigned-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assigned To</label>
                <input type="date" value={assignedToDate} onChange={e => { setAssignedToDate(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="ota-dash-assigned-to" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Completed From</label>
                <input type="date" value={completedFrom} onChange={e => { setCompletedFrom(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="ota-dash-completed-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Completed To</label>
                <input type="date" value={completedToDate} onChange={e => { setCompletedToDate(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="ota-dash-completed-to" />
              </div>
            </div>
            {hasActiveFilters && (
              <button onClick={clearAllFilters}
                className="text-xs text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1" data-testid="ota-dash-clear-filters"><X size={12} /> Clear all</button>
            )}
          </div>
        )}
      </div>

      {/* Primary Analytics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Listings', value: a.total_listings || 0, color: 'teal', icon: Globe2 },
          { label: 'Assigned', value: a.total_assigned || 0, color: 'blue', icon: User },
          { label: 'In Progress', value: a.in_progress || 0, color: 'amber', icon: Clock },
          { label: 'Completed', value: `${a.completed || 0} (${a.completion_pct || 0}%)`, color: 'emerald', icon: CheckCircle },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid={`ota-kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className={`w-9 h-9 rounded-lg bg-${k.color}-50 flex items-center justify-center mb-2`}>
              <k.icon size={18} className={`text-${k.color}-500`} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{k.value}</p>
            <p className="text-xs text-gray-500">{k.label}</p>
          </div>
        ))}
      </div>

      {/* SLA KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="ota-kpi-late">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><ShieldAlert size={18} className="text-red-500" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{a.late_count || 0}</p>
          <p className="text-xs text-gray-500">Late Completions</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="ota-kpi-on-time-pct">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp size={18} className="text-emerald-500" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{a.on_time_pct || 0}%</p>
          <p className="text-xs text-gray-500">On-Time Rate</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="ota-kpi-overdue">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center"><AlertTriangle size={18} className="text-orange-500" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{a.overdue_count || 0}</p>
          <p className="text-xs text-gray-500">Currently Overdue</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="ota-kpi-avg-time">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Timer size={18} className="text-blue-500" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{a.avg_completion_hours != null ? `${a.avg_completion_hours}h` : '-'}</p>
          <p className="text-xs text-gray-500">Avg Completion Time</p>
        </div>
      </div>

      {/* OTA Checklist SLA Distribution */}
      {checklistAnalytics && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="ota-checklist-analytics">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <ClipboardList size={16} className="text-teal-500" />
            <h3 className="text-sm font-semibold text-gray-800">OTA Checklist SLA Overview</h3>
            <span className="text-xs text-gray-400 ml-auto">{checklistAnalytics.total_listings} listings</span>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
              {[
                { label: 'Checklist Complete', value: checklistAnalytics.checklist_complete, color: 'teal', icon: CheckCircle },
                { label: 'Expected (0-2d)', value: checklistAnalytics.sla_distribution?.expected || 0, color: 'emerald', icon: Clock },
                { label: 'Urgent (2-5d)', value: checklistAnalytics.sla_distribution?.urgent || 0, color: 'amber', icon: AlertTriangle },
                { label: 'Critical (>5d)', value: checklistAnalytics.sla_distribution?.critical || 0, color: 'red', icon: AlertOctagon },
                { label: 'Completion %', value: `${checklistAnalytics.completion_pct}%`, color: 'blue', icon: BarChart3 },
              ].map(k => (
                <div key={k.label} className="rounded-lg p-3 border border-gray-100 bg-gray-50" data-testid={`checklist-sla-${k.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <k.icon size={14} className={`text-${k.color}-500`} />
                    <span className="text-[11px] text-gray-500 font-medium">{k.label}</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{k.value}</p>
                </div>
              ))}
            </div>
            {/* Per-item completion bars */}
            {checklistAnalytics.per_item_stats && checklistAnalytics.per_item_stats.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-600 mb-2">Per-Item Completion</p>
                {checklistAnalytics.per_item_stats.map(item => (
                  <div key={item.key} className="flex items-center gap-3" data-testid={`checklist-item-stat-${item.key}`}>
                    <span className="text-xs text-gray-600 w-44 shrink-0 truncate">{item.label}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${item.pct}%` }} />
                    </div>
                    <span className="text-xs font-medium text-gray-500 w-14 text-right">{item.complete}/{item.complete + item.pending}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Listings Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Table Header: Info | Search | Pagination */}
        {!grouped && (
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 shrink-0 min-w-[180px]">
                <span>Showing {assignments.length} of {totalRecords}</span>
                <span className="text-gray-300">|</span>
                <span>Page {page}/{totalPages}</span>
              </div>
              <div className="w-full sm:flex-1 sm:max-w-sm mx-auto">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Search listings..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none" data-testid="ota-dash-search" />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="hidden sm:inline">Per page:</span>
                  <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                    className="px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-xs" data-testid="ota-page-size">
                    {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="ota-prev-page">Prev</button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="ota-next-page">Next</button>
                </div>
              </div>
            </div>
            <div className="sm:hidden flex items-center justify-between text-xs text-gray-500 mt-2">
              <span>Showing {assignments.length} of {totalRecords}</span>
              <span>Page {page}/{totalPages}</span>
            </div>
          </div>
        )}
        {grouped && (
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="w-full max-w-sm mx-auto">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search listings..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none" data-testid="ota-dash-search" />
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 size={28} className="animate-spin text-teal-500" /></div>
        ) : grouped ? (
          /* Grouped View */
          <div data-testid="ota-grouped-view">
            {Object.entries(grouped).sort((a, b) => b[1].length - a[1].length).map(([key, items]) => {
              const expanded = expandedGroups[key] !== false;
              const done = items.filter(i => i.assignment_status === 'completed').length;
              const lateInGroup = items.filter(i => i.sla_status === 'late' || i.sla_status === 'overdue').length;
              return (
                <div key={key} className="border-b border-gray-100 last:border-0">
                  <button onClick={() => toggleGroup(key)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid={`group-${key}`}>
                    <div className="flex items-center gap-3">
                      {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      <span className="font-semibold text-gray-800">{key}</span>
                      <span className="text-xs text-gray-400">{items.length} listings</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {lateInGroup > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{lateInGroup} late</span>}
                      <span className="text-xs text-emerald-600 font-medium">{done} done</span>
                      <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${items.length > 0 ? (done / items.length * 100) : 0}%` }} />
                      </div>
                    </div>
                  </button>
                  {expanded && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        {tableHead}
                        <tbody className="divide-y divide-gray-50">
                          {items.map(item => <EditableOTARow key={`${item.lead_id}-${item.ota_index}`} item={item} onSave={handleSave} />)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : assignments.length > 0 ? (
          /* Flat View */
          <div className="overflow-x-auto" data-testid="ota-flat-view">
            <table className="w-full text-sm">
              {tableHead}
              <tbody className="divide-y divide-gray-100">
                {assignments.map(item => <EditableOTARow key={`${item.lead_id}-${item.ota_index}`} item={item} onSave={handleSave} />)}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <Globe2 size={40} className="mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-semibold text-gray-800 mb-1">No OTA Listings</h3>
            <p className="text-sm text-gray-500">{hasActiveFilters ? 'Try adjusting your filters' : 'No OTA assignments found'}</p>
          </div>
        )}

        {/* Pagination is in table header */}
      </div>

      {/* User Performance Widget */}
      <UserPerformanceWidget data={a.user_performance} />
    </div>
  );
};

export default OTADashboard;
